import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Check, X, AlertCircle, Info, Download, Copy, RefreshCw } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  icon?: React.ReactNode;
}

interface ToastContextType {
  showToast: (type: ToastType, message: string, icon?: React.ReactNode) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Helper functions for common toast types
export const toastHelpers = {
  copied: (showToast: ToastContextType['showToast']) =>
    showToast('success', 'Copied to clipboard', <Copy size={14} />),
  exported: (showToast: ToastContextType['showToast'], format: string) =>
    showToast('success', `Exported as ${format.toUpperCase()}`, <Download size={14} />),
  refreshed: (showToast: ToastContextType['showToast']) =>
    showToast('info', 'Data refreshed', <RefreshCw size={14} />),
  error: (showToast: ToastContextType['showToast'], message: string) =>
    showToast('error', message, <AlertCircle size={14} />),
};

const ToastItem: React.FC<{ toast: Toast; onRemove: (id: string) => void }> = ({
  toast,
  onRemove,
}) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onRemove(toast.id), 200);
    }, 3000);

    return () => clearTimeout(timer);
  }, [toast.id, onRemove]);

  const icons: Record<ToastType, React.ReactNode> = {
    success: <Check size={14} className="text-data-green" />,
    error: <AlertCircle size={14} className="text-data-red" />,
    warning: <AlertCircle size={14} className="text-data-amber" />,
    info: <Info size={14} className="text-data-blue" />,
  };

  const bgColors: Record<ToastType, string> = {
    success: 'border-data-green/30 bg-data-green/10',
    error: 'border-data-red/30 bg-data-red/10',
    warning: 'border-data-amber/30 bg-data-amber/10',
    info: 'border-data-blue/30 bg-data-blue/10',
  };

  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 bg-terminal-panel border rounded shadow-lg transition-all duration-200 ${
        bgColors[toast.type]
      } ${isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}`}
    >
      {toast.icon || icons[toast.type]}
      <span className="text-[11px] text-white">{toast.message}</span>
      <button
        onClick={() => {
          setIsExiting(true);
          setTimeout(() => onRemove(toast.id), 200);
        }}
        className="ml-2 text-gray-500 hover:text-white transition-colors"
      >
        <X size={12} />
      </button>
    </div>
  );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback(
    (type: ToastType, message: string, icon?: React.ReactNode) => {
      const id = Date.now().toString() + Math.random().toString(36).slice(2);
      setToasts((prev) => [...prev, { id, type, message, icon }]);
    },
    []
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* Toast Container */}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
